import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { Guid } from "client_legacy/ndaw/guid";
import {
	MagisterConnectionInterests,
	MagisterLiveInformationResponseModel,
	LiveInformationRequestModel,
	getLiveInformation,
	ScheduleResponseModel,
	GradesResponseModel
} from "client/Api/Workspace/LiveInformation/Magister/magisterLiveInformation";
import { ensureNotNil } from "client/Misc/utils";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { userProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatForDto, parseDtoDate } from "client/DateAndTime/dateTimeUtils";

export type MagisterLiveInformationProviderOptions = {
	intervalInSeconds: number;
};

export type MagisterResourceId = {
	connectionId: number;
	date: Date;
};

export interface InterestedMagisterParty extends InterestedParty<Guid, MagisterResourceId> {
	updateGrades?(response: GradesResponseModel, isTeacher: boolean | null): void;
	updateDaySchedule?(response: ScheduleResponseModel, isTeacher: boolean | null): void;
	updateWeekSchedule?(response: ScheduleResponseModel, isTeacher: boolean | null): void;
}

export class MagisterLiveInformationProvider extends LiveInformationProvider<
	MagisterLiveInformationProviderOptions,
	Guid,
	MagisterResourceId,
	MagisterConnectionInterests,
	InterestedMagisterParty,
	MagisterLiveInformationResponseModel
> {

	constructor(options: MagisterLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: readonly [MagisterResourceId, MagisterConnectionInterests][]
	): Promise<readonly [MagisterResourceId, MagisterLiveInformationResponseModel][]> {
		const { userTimezone } = userProfileStore.getState();
		const requests: LiveInformationRequestModel[] = entries.map(
			entry => this.convertResourceIdToRequest(
				entry[0],
				entry[1],
				userTimezone
			)
		);
		const responses: readonly MagisterLiveInformationResponseModel[] = await getLiveInformation(requests);

		return responses.map(response => [this.convertResponseToResourceId(response), response]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<MagisterConnectionInterests, MagisterLiveInformationResponseModel>,
		response: [MagisterConnectionInterests, MagisterLiveInformationResponseModel]
	): void {
		const data: MagisterLiveInformationResponseModel = response[1];

		if (data.interests.grades) {
			cacheItem.retrievedInterests.grades = true;
			cacheItem.data.data.grades = data.data.grades;
		}

		if (data.interests.daySchedule) {
			cacheItem.retrievedInterests.daySchedule = true;
			cacheItem.data.data.daySchedule = data.data.daySchedule;
		}

		if (data.interests.weekSchedule) {
			cacheItem.retrievedInterests.weekSchedule = true;
			cacheItem.data.data.weekSchedule = data.data.weekSchedule;
		}

		cacheItem.data.isTeacher = data.isTeacher;
	}

	protected informParty(
		party: InterestedMagisterParty,
		cacheItem: [MagisterConnectionInterests, MagisterLiveInformationResponseModel]
	): void {
		const [retrievedInterests, response] = cacheItem;
		const interestedIn: MagisterConnectionInterests = this.getInterestsForEnsuredParty(party);

		const {
			grades,
			daySchedule,
			weekSchedule
		} = response.data;

		if (interestedIn.grades
			&& retrievedInterests.grades
		)
		{
			party.updateGrades?.(
				ensureNotNil(grades),
				response.isTeacher
			);
		}

		if (interestedIn.daySchedule
			&& retrievedInterests.daySchedule
		)
		{
			party.updateDaySchedule?.(
				ensureNotNil(daySchedule),
				response.isTeacher
			);
		}

		if (
			interestedIn.weekSchedule
			&& retrievedInterests.weekSchedule
		)
		{
			party.updateWeekSchedule?.(
				ensureNotNil(weekSchedule),
				response.isTeacher
			);
		}
	}

	protected override createResourceMap<TValue>(): Map<MagisterResourceId, TValue> {
		return new NormalizedMap<MagisterResourceId, TValue>(
			(key) => `${key.connectionId}_${key.date}`
		);
	}

	private convertResourceIdToRequest(
		resourceId: MagisterResourceId,
		interestedIn: MagisterConnectionInterests,
		timezone: TimeZone
	): LiveInformationRequestModel {

		return {
			connectionId: resourceId.connectionId,
			// We want the local timezone so we cannot use .toISOString which always outputs UTC
			date: formatForDto(resourceId.date, timezone),
			interests: interestedIn
		};
	}

	private convertResponseToResourceId(
		response: MagisterLiveInformationResponseModel
	): MagisterResourceId {
		return {
			connectionId: response.connectionId,
			date: parseDtoDate(response.date)
		};
	}
}
